// admin-web/src/pages/MessagingTruncate.tsx
import { httpsCallable } from "firebase/functions";
import { useMemo, useState } from "react";
import { functions } from "../firebase";

type DeleteThreadsSummary = {
  mode: "brokenTestOnly" | "byIds" | "all";
  dryRun: boolean;
  scanned: number;
  matched: number;
  deleted: number;
};

type DeleteThreadsRow = {
  threadId: string;
  participantIds: string[];
  studentKeys: string[];
  reason: string;
};

type DeleteThreadsResponse = {
  summary: DeleteThreadsSummary;
  rows: DeleteThreadsRow[];
};

function prettyJson(obj: any) {
  return JSON.stringify(obj, null, 2);
}

export function MessagingTruncate() {
  const [mode, setMode] = useState<"brokenTestOnly" | "all">("brokenTestOnly");
  const [limit, setLimit] = useState<number>(500);

  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const [result, setResult] = useState<DeleteThreadsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [repairResult, setRepairResult] = useState<any>(null);

  const canDeleteAll =
    mode === "all" && confirmText.trim().toUpperCase() === "DELETE ALL THREADS";
  const canDeleteBroken =
    mode === "brokenTestOnly" && confirmText.trim().toUpperCase() === "DELETE";

  const confirmOk = mode === "all" ? canDeleteAll : canDeleteBroken;

  const rowsPreview = useMemo(() => {
    if (!result?.rows?.length) return [];
    return result.rows.slice(0, 50);
  }, [result]);

  async function run(dryRun: boolean) {
    setBusy(true);
    setError(null);

    try {
      const fn = httpsCallable(functions, "deleteThreads");
      const res: any = await fn({
        dryRun,
        mode,
        limit,
      });

      setResult(res.data as any);
    } catch (e: any) {
      setError(e?.message || "Failed to run deleteThreads.");
      setResult(null);
    } finally {
      setBusy(false);
    }
  }

  async function runRepair(kind: "repairClubInboxIndex" | "rebuildMyInboxIndex") {
    setBusy(true);
    setError(null);
    setRepairResult(null);

    try {
      const fn = httpsCallable(functions, kind);
      const res: any = await fn({});
      setRepairResult({ kind, ...(res?.data ?? {}) });
    } catch (e: any) {
      setError(e?.message || `Failed to run ${kind}.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>Messaging Maintenance</h2>
      <p className="muted" style={{ marginTop: 6 }}>
        Use these tools to repair inbox indexes and remove malformed messaging threads when needed.
      </p>

      {error ? <div className="error" style={{ marginTop: 10 }}>{error}</div> : null}

      <div className="card" style={{ marginTop: 12, background: "rgba(0,0,0,0.02)" }}>
        <h3 style={{ marginTop: 0 }}>Inbox Index Repair</h3>
        <p className="muted" style={{ marginTop: 6 }}>
          Runs Cloud Functions as the signed-in admin to rebuild your own userInboxes/{`{uid}`}/threads docs.
        </p>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="btn secondary" disabled={busy} onClick={() => runRepair("repairClubInboxIndex")}>
            Repair Club Inbox Index
          </button>
          <button className="btn secondary" disabled={busy} onClick={() => runRepair("rebuildMyInboxIndex")}>
            Rebuild My Inbox Index
          </button>
        </div>

        {repairResult ? (
          <pre style={{ marginTop: 10, whiteSpace: "pre-wrap" }}>{prettyJson(repairResult)}</pre>
        ) : null}
      </div>

      <hr className="hr" />

      <h3>Delete Threads Tool</h3>
      <p className="muted" style={{ marginTop: 6 }}>
        This is destructive. Use dry run first. The targeted cleanup mode only selects malformed threads.
      </p>

      <div className="grid2">
        <div>
          <label className="label">Mode</label>
          <select
            className="input"
            value={mode}
            onChange={(e) => setMode(e.target.value as any)}
            disabled={busy}
          >
            <option value="brokenTestOnly">Targeted cleanup</option>
            <option value="all">All threads</option>
          </select>
        </div>

        <div>
          <label className="label">Limit</label>
          <input
            className="input"
            type="number"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value || 0))}
            disabled={busy}
          />
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <label className="label">Confirm</label>
        <input
          className="input"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          disabled={busy}
          placeholder={mode === "all" ? "DELETE ALL THREADS" : "DELETE"}
        />
        <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
          Type the exact phrase above to enable actions.
        </div>
      </div>

      <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button className="btn secondary" disabled={busy || !confirmOk} onClick={() => run(true)}>
          {busy ? "Working…" : "Dry Run"}
        </button>
        <button className="btn" disabled={busy || !confirmOk} onClick={() => run(false)}>
          {busy ? "Working…" : "Delete"}
        </button>
      </div>

      {result ? (
        <div style={{ marginTop: 14 }}>
          <h3>Result</h3>
          <pre style={{ whiteSpace: "pre-wrap" }}>{prettyJson(result.summary)}</pre>

          <h4>Preview (first 50)</h4>
          <pre style={{ whiteSpace: "pre-wrap" }}>{prettyJson(rowsPreview)}</pre>
        </div>
      ) : null}
    </div>
  );
}
